import { useState, useEffect, useRef, useMemo } from "react";
import {
  computeGameCharge, planFromSession, resolveRateAtInstant, sessionPauses, sessionPlanPhases,
} from "@/lib/billing";

/**
 * Live bill for a running session, mirroring the backend billing engine.
 *
 * Pass the session (preferred) so the pricing plan snapshotted at start is used
 * — the bill then splits automatically across Happy Hour boundaries, exactly as
 * the backend charges on stop. Paused time is excluded, so while the game is
 * paused both the bill and the play clock stay frozen. A plain hourly rate is
 * still accepted for backward compatibility.
 *
 * Returns { amount, rate, label, segments, stretches, playedMs, billedMinutes,
 * paused } — `label` is the pricing window in effect right now ("Normal",
 * "Happy Hour", ...), `stretches` is the bill table by table for a game that
 * moved, `playedMs` drives the timer, so the clock and the bill share one source,
 * and `billedMinutes` is what a bill should say was played.
 */
export function useLiveBill(sessionOrStartTime, pricePerHour) {
  const [state, setState] = useState({
    amount: 0, rate: 0, label: "Normal", segments: [], stretches: [], playedMs: 0, billedMinutes: 0, paused: false,
  });
  const intervalRef = useRef(null);

  // Accept either a session object or the legacy (startTime, pricePerHour) pair.
  const { game, pauseSource } = useMemo(() => {
    if (sessionOrStartTime && typeof sessionOrStartTime === "object") {
      const s = sessionOrStartTime;
      return {
        // Only what the bill depends on, so a tab edit doesn't restart the clock.
        game: {
          start_time: s.start_time,
          price_per_hour: s.price_per_hour,
          pricing_plan: s.pricing_plan,
          pricing_plans: s.pricing_plans,
          controller_changes: s.controller_changes,
          table_moves: s.table_moves,
          table_name: s.table_name,
        },
        pauseSource: {
          pauses: s.pauses || [],
          paused_at: s.paused_at || null,
        },
      };
    }
    return {
      game: { start_time: sessionOrStartTime, price_per_hour: pricePerHour },
      pauseSource: { pauses: [], paused_at: null },
    };
  }, [sessionOrStartTime, pricePerHour]);

  // Stable keys so the effect doesn't restart on every render (objects are rebuilt each time).
  const gameKey = JSON.stringify(game);
  const pauseKey = JSON.stringify(pauseSource);

  useEffect(() => {
    const activeGame = JSON.parse(gameKey);
    if (!activeGame.start_time) return;
    const pauseInfo = JSON.parse(pauseKey);
    const paused = !!pauseInfo.paused_at;
    const phases = sessionPlanPhases(activeGame);
    // The plan the next minute will be charged on — the latest phase, if any.
    const currentPlan = phases.length ? phases[phases.length - 1][1] : planFromSession(activeGame);

    const update = () => {
      const now = Date.now();
      const { totalAmount, segments, stretches, playedMs, billedMinutes } = computeGameCharge(
        activeGame, now, sessionPauses(pauseInfo, now),
      );
      const { rate, label } = resolveRateAtInstant(currentPlan, now);
      setState({ amount: totalAmount, rate, label, segments, stretches, playedMs, billedMinutes, paused });
    };

    update();
    // Nothing moves while paused, so don't tick; resuming changes pauseKey and
    // restarts the effect.
    if (!paused) intervalRef.current = setInterval(update, 1000);
    return () => clearInterval(intervalRef.current);
  }, [gameKey, pauseKey]);

  return state;
}
